import { useCallback, useRef, useState } from 'react';
import { signOut } from '../utils/firebase';

/**
 * Shared logout-button behavior for every active logout control (Sidebar,
 * Drawer, Profile, Settings). Centralizes ONLY this control-level concern so
 * the four surfaces can't drift into inconsistent implementations:
 *
 *   - single-flight: signOut() runs at most once, even under rapid clicks
 *   - a `loggingOut` pending flag for the "Signing out…" / disabled UI
 *   - the Firebase signOut() call
 *   - a `finally` that runs the caller's logged-out navigation AND always
 *     clears the pending flag — so a failed logout can never leave the
 *     control permanently disabled ("Signing out…" stuck)
 *
 * This is NOT an auth refactor: the authoritative auth-state lifecycle
 * (onAuthStateChanged → setUser, realtime teardown) stays in AuthContext.
 * The single-flight guard uses a ref so it holds even for same-tick rapid
 * clicks (before a re-render disables the button).
 *
 * @param onComplete runs in `finally` after signOut resolves OR rejects — put
 *        the surface's existing logged-out navigation here (it must run in
 *        both cases, matching the prior "navigate away regardless" behavior).
 */
export function useLogout(onComplete: () => void): { loggingOut: boolean; logout: () => Promise<void> } {
  const [loggingOut, setLoggingOut] = useState(false);
  const inFlight = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete; // always call the latest callback without re-creating `logout`

  const logout = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoggingOut(true);
    try {
      await signOut();
    } catch {
      /* fall through — navigate away regardless */
    } finally {
      onCompleteRef.current();
      inFlight.current = false;
      setLoggingOut(false);
    }
  }, []);

  return { loggingOut, logout };
}
